/**
 * Tier Access Hook
 *
 * Check if the current user's tier allows access to a specific menu/feature.
 * Reads from the cached menuAccess in the auth store.
 *
 * Usage:
 *   const { allowed } = useTierAccess('my_artworks');
 *   if (!allowed) return <UpgradePrompt />;
 */

import { useAuthStore } from '@/stores/authStore';

interface TierAccessResult {
  allowed: boolean;
  tier: string;
  isAuthenticated: boolean;
}

export function useTierAccess(menuKey: string): TierAccessResult {
  const { user, isAuthenticated, menuAccess } = useAuthStore();
  const tier = user?.tier || 'free';

  // Not authenticated — can't check tier access, default to allowed (public pages handle auth separately)
  if (!isAuthenticated) {
    return { allowed: true, tier: 'free', isAuthenticated: false };
  }

  // Admins bypass all tier restrictions
  const isAdmin = user?.role === 'platform_admin' || user?.role === 'superadmin';
  if (isAdmin) {
    return { allowed: true, tier, isAuthenticated: true };
  }

  // Check menu access from cached store
  const allowed = menuAccess[menuKey] !== false; // Default to true if key not found (safety)

  return { allowed, tier, isAuthenticated: true };
}
